import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Stack,
  Container,
  Typography,
  Chip,
  Button,
  Grid,
  Box,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import { toast } from 'react-toastify';

import Iconify from '../../components/iconify';
import { API_URL } from '../../api';

const cookies = new Cookies();

export default function CourseOrderDetails() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [notes, setNotes] = useState('');

  const [refundDialog, setRefundDialog] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/course-orders/admin/${id}`, {
        headers: {
          Authorization: cookies.get('TOKEN'),
        },
      });
      setOrder(response.data.data);
      setNotes(response.data.data.notes || '');
      setLoading(false);
    } catch (error) {
      console.error('Error fetching course order:', error);
      toast.error('Failed to load order');
      navigate('/course-orders');
    }
  };

  const formatPrice = (price) => {
    return `£${(price / 100).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCourseDate = (date) => {
    if (!date) return 'TBC';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleRefund = async () => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/course-orders/admin/${id}/refund`,
        { reason: refundReason },
        {
          headers: {
            Authorization: cookies.get('TOKEN'),
          },
        }
      );
      toast.success('Refund processed successfully');
      setRefundDialog(false);
      fetchOrder();
    } catch (error) {
      console.error('Error processing refund:', error);
      toast.error(error.response?.data?.message || 'Failed to process refund');
    }
    setActionLoading(false);
  };

  const handleSaveNotes = async () => {
    try {
      await axios.patch(
        `${API_URL}/api/course-orders/admin/${id}/notes`,
        { notes },
        {
          headers: {
            Authorization: cookies.get('TOKEN'),
          },
        }
      );
      toast.success('Notes saved');
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Alert severity="error">Order not found</Alert>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Course Order {order.orderNumber} | English Teacher Admin</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Box>
            <Button
              startIcon={<Iconify icon="mdi:arrow-left" />}
              onClick={() => navigate('/course-orders')}
              sx={{ mb: 1 }}
            >
              Back to Course Orders
            </Button>
            <Typography variant="h4" sx={{ fontFamily: 'Inter', fontWeight: '600' }}>
              Course Order {order.orderNumber}
            </Typography>
          </Box>
          <Box>
            {order.paymentStatus === 'paid' && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<Iconify icon="mdi:cash-refund" />}
                onClick={() => setRefundDialog(true)}
              >
                Refund
              </Button>
            )}
          </Box>
        </Stack>

        <Grid container spacing={3}>
          {/* Order Status */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    label={order.paymentStatus}
                    color={getStatusColor(order.paymentStatus)}
                    sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Placed on {formatDate(order.createdAt)}
                  </Typography>
                  {order.refundedAt && (
                    <Typography variant="body2" color="error">
                      Refunded on {formatDate(order.refundedAt)}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Course Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Course Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {order.courseTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Date: {formatCourseDate(order.courseDate)}
                </Typography>
                {order.courseTime && (
                  <Typography variant="body2" color="textSecondary">
                    Time: {order.courseTime}
                  </Typography>
                )}
                {order.courseLocation && (
                  <Typography variant="body2" color="textSecondary">
                    Location: {order.courseLocation}
                  </Typography>
                )}
                {order.courseCost && (
                  <Typography variant="body2" color="textSecondary">
                    Display Cost: {order.courseCost}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Customer Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Customer Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" fontWeight={500}>
                  {order.customer.parentName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {order.customer.email}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2">
                  <strong>Child:</strong> {order.customer.childFullName}
                </Typography>
                {order.customer.yearGroup && (
                  <Typography variant="body2" color="textSecondary">
                    Year Group: {order.customer.yearGroup}
                  </Typography>
                )}
                {order.customer.schoolName && (
                  <Typography variant="body2" color="textSecondary">
                    School: {order.customer.schoolName}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {formatPrice(order.amount)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Payment Method
                  </Typography>
                  <Chip
                    icon={<Iconify icon="mdi:credit-card" />}
                    label="Card"
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {order.stripePaymentIntentId && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Stripe ID
                    </Typography>
                    <Typography variant="body2" fontFamily="monospace">
                      {order.stripePaymentIntentId.slice(0, 20)}...
                    </Typography>
                  </Box>
                )}
                {order.refundAmount && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Refund Amount
                    </Typography>
                    <Typography variant="body2" color="error">
                      {formatPrice(order.refundAmount)}
                    </Typography>
                  </Box>
                )}
                {order.emailSent && (
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Confirmation Email
                    </Typography>
                    <Chip label="Sent" color="success" size="small" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Admin Notes */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Admin Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add internal notes about this order..."
                />
                <Button variant="outlined" sx={{ mt: 2 }} onClick={handleSaveNotes}>
                  Save Notes
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Refund Dialog */}
        <Dialog open={refundDialog} onClose={() => setRefundDialog(false)}>
          <DialogTitle>Process Refund</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will refund {formatPrice(order.amount)} to the customer.
              This action cannot be undone.
            </DialogContentText>
            <TextField
              fullWidth
              label="Refund Reason (optional)"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              margin="normal"
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRefundDialog(false)}>Cancel</Button>
            <Button onClick={handleRefund} variant="contained" color="error" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={24} /> : 'Process Refund'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
}
